import { DefaultOptionType } from "antd/es/select";
import { observer } from "mobx-react-lite";
import type { BaseSelectRef } from "rc-select";
import * as React from "react";
import {
  availableParentsLoader,
  ClassParentsFilter,
  ZClassUnsafe,
} from "src/business/classes";
import {
  makeHierarchicalLabel,
  SysParentsColumn,
} from "src/business/sysData/SysParentsColumn";
import { onError } from "src/common/onError";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { RefSelect } from "src/components/RefSelect/RefSelect";
import { RefSelectStore } from "src/components/RefSelect/RefSelectStore";
import {
  ActivateCallback,
  useActivateCallback,
} from "src/types/ActivateCallback";

import { makeAttrSelectOptions } from "../makeAttrSelectOptions";
import { ZAttrMeta } from "../ZAttrMeta";
import { ZAttrParentsSelect } from "./ZAttrComponent";

// Определяем колонки для таблицы в Drawer
const columns: RtColumn<ZClassUnsafe>[] = [
  {
    key: "id",
    title: "ID",
    render: (row) => row.id,
  },
  {
    key: "name",
    title: "Название",
    render: (row) => row.name,
  },
  {
    key: "parents",
    title: "Родители",
    render: (row: ZClassUnsafe) => <SysParentsColumn parents={row.parents} />,
  },
];

type PropsAttrEditParentsSelect = {
  attrMeta: ZAttrMeta;
  value: unknown[] | unknown;
  onChange?(newValue: unknown): void;
  disabled?: boolean;
  activateCallback?: ActivateCallback;
  component: ZAttrParentsSelect;
  textCvt?(text: string): string;
  onBlur(): void;
};

export const AttrEditParentsSelect: React.FC<PropsAttrEditParentsSelect> =
  observer((props) => {
    const {
      value,
      onChange,
      disabled,
      component,
      attrMeta,
      textCvt,
      activateCallback,
      onBlur,
    } = props;
    const ctrlRef = useActivateCallback<BaseSelectRef>(
      activateCallback,
      (ref) => () => ref?.focus(),
    );
    const [list, setList] = React.useState<DefaultOptionType[]>([]);
    const [loading, setLoading] = React.useState(false);

    const cvt = (text: string | null | undefined) => {
      if (!text) return undefined;
      return textCvt?.(text) ?? text;
    };
    const placeholder =
      cvt(component.placeholder) ||
      cvt(attrMeta.title) ||
      "Выберите родительский класс";
    const drawerTitle =
      cvt(component.drawerTitle) ||
      cvt(attrMeta.title) ||
      "Выбор родительского класса";

    React.useEffect(() => {
      setLoading(true);
      makeAttrSelectOptions(component.options)
        .then((optList) => {
          setList(
            optList.map((option) => ({
              label: option.label || option.id,
              value: option.id,
            })),
          );
        })
        .finally(() => {
          setLoading(false);
        })
        .catch(onError);
    }, [component.options]);

    // к сожалению сейчас проблематично сделать передачу сторов через пропсы
    const tableStore = React.useMemo(
      () =>
        new RtStore<ZClassUnsafe, ClassParentsFilter>({
          loader: availableParentsLoader(
            component.classId ? String(component.classId) : undefined,
            component.parentId ? String(component.parentId) : undefined,
          ),
          // Множественный выбор родителей
          selectionType: "checkbox",
          rowKey: "id",
        }),
      [component.classId],
    );

    const refSelectStore = React.useMemo(
      () =>
        new RefSelectStore({
          tableStore,
          initialOptions: list,
          renderLabel: (row) => makeHierarchicalLabel(row.parents, row.name),
          drawerTitle,
        }),
      [tableStore, list],
    );

    return (
      <RefSelect
        ref={ctrlRef}
        loading={loading}
        refSelectStore={refSelectStore}
        columns={columns}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        allowClear
        onBlur={onBlur}
      />
    );
  });
